// Registros firmados cuyo origen se borró (trabajador, capacitación, examen o asignación de EPP): se conservan como
// evidencia (M45, FK con ON DELETE SET NULL) y se listan aquí, con el nombre congelado y la marca "(trabajador
// eliminado)". Se abren con RegistroFirmasModal para ver el contenido, descargar el PDF o, el administrador, reabrir.
// Los que no tienen ninguna firma (y no están firmados) se pueden borrar (admin o manager): la M45 lo permite y así
// no quedan huérfanos sin valor de evidencia.

import React, { useCallback, useEffect, useState } from 'react';
import RegistroFirmasModal from './RegistroFirmasModal';
import { fechaEc } from '../../lib/fechas';
import { borrarRegistro, cargarFirmas, type DatosFirmas } from '../../lib/firmas/firmas-datos';
import { estadoVisible, puedeBorrarRegistro, registroSinOrigen, registroSinTrabajador } from '../../lib/firmas/firmas-logica';
import { ETIQUETA_TIPO_REGISTRO, MARCA_TRABAJADOR_ELIMINADO, type FilaRegistro, type TipoRegistro } from '../../lib/firmas/firmas-tipos';
import { plural } from '../../lib/plurales';

interface Props {
  companyId: string;
  tipo: TipoRegistro;
  usuarioId: string | null;
  puedeEditar: boolean;
  esAdmin: boolean;
  /** Cambia cuando la pantalla recarga sus datos (p. ej. después de borrar un trabajador). */
  recarga?: unknown;
}

// Fecha sin hora, tal cual; un instante, en el día de Ecuador (lib/fechas).
const fecha = (iso: string) => fechaEc(iso);

/** Nombre para la lista: el del contenido congelado, con la marca si el trabajador ya no existe. */
export function nombreConservado(r: FilaRegistro): string {
  const c = r.snapshot;
  if (!c) return ETIQUETA_TIPO_REGISTRO[r.kind];
  if (c.tipo === 'asistencia' || c.tipo === 'socializacion') return `${c.titulo} · ${fecha(c.fecha)}`;
  return `${c.trabajador}${registroSinTrabajador(r) ? ` ${MARCA_TRABAJADOR_ELIMINADO}` : ''} · ${fecha(c.fecha)}`;
}

export default function RegistrosConservados(p: Props) {
  const [datos, setDatos] = useState<DatosFirmas>({ registros: [], firmas: [], itemsEpp: [] });
  const [abierto, setAbierto] = useState<FilaRegistro | null>(null);
  const [borrando, setBorrando] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    if (!p.companyId) return;
    setDatos(await cargarFirmas(p.companyId, p.tipo).catch(() => ({ registros: [], firmas: [], itemsEpp: [] })));
  }, [p.companyId, p.tipo]);
  useEffect(() => { cargar(); }, [cargar, p.recarga]);

  // Sin origen: la capacitación, el trabajador o el examen ya no existen; en EPP, también si ninguna asignación sigue viva.
  const conservados = datos.registros.filter(r => r.snapshot && (registroSinOrigen(r)
    || (r.kind === 'epp_entrega' && !datos.itemsEpp.some(i => i.record_id === r.id && i.epp_assignment_id))));
  if (!conservados.length) return null;
  const firmasDe = (id: string) => datos.firmas.filter(f => f.record_id === id);
  const borrar = async (r: FilaRegistro) => {
    if (!window.confirm('¿Borrar este registro? No tiene ninguna firma.')) return;
    setBorrando(r.id);
    try {
      await borrarRegistro(r);
      await cargar();
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setBorrando(null);
    }
  };

  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <h3 style={{ color: '#e2e8f0', fontSize: '0.92rem', fontWeight: 700, margin: 0 }}>Registros firmados conservados</h3>
      <p style={{ color: '#94a3b8', fontSize: '0.78rem', margin: 0 }}>
        {plural(conservados.length, 'registro cuyo origen se borró', 'registros cuyo origen se borró')}. Se conservan como evidencia con el
        nombre y la cédula del contenido firmado.
      </p>
      {conservados.map(r => {
        const est = estadoVisible(r, firmasDe(r.id));
        return (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', padding: '8px 0', borderTop: '1px solid rgba(51,65,85,0.6)' }}>
            <span style={{ color: '#e2e8f0', fontSize: '0.84rem', minWidth: 0, flex: '1 1 200px', overflowWrap: 'anywhere' }}>{nombreConservado(r)}</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => setAbierto(r)}
                style={{ padding: '8px 12px', minHeight: 40, background: 'transparent', border: '1px solid #475569', color: '#cbd5e1', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                {est.texto} · Ver
              </button>
              {p.puedeEditar && puedeBorrarRegistro(r, firmasDe(r.id).length) && (
                <button type="button" onClick={() => borrar(r)} disabled={borrando === r.id}
                  style={{ padding: '8px 12px', minHeight: 40, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171', borderRadius: 8, cursor: borrando === r.id ? 'not-allowed' : 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                  {borrando === r.id ? 'Borrando…' : 'Borrar'}
                </button>
              )}
            </div>
          </div>
        );
      })}
      {abierto && (
        <RegistroFirmasModal
          titulo={nombreConservado(abierto)}
          companyId={p.companyId}
          usuarioId={p.usuarioId}
          registro={abierto}
          firmas={firmasDe(abierto.id)}
          origen={{}}
          armarContenido={async () => {
            if (!abierto.snapshot) throw new Error('El registro no tiene contenido.');
            return { contenido: abierto.snapshot, faltantes: ['El origen del registro se borró: no se puede volver a preparar para firma.'] };
          }}
          puedeEditar={p.puedeEditar}
          esAdmin={p.esAdmin}
          onCambio={cargar}
          onCerrar={() => setAbierto(null)}
        />
      )}
    </div>
  );
}
